// UserGeneralDetailsComponent.ts
import {ActiveUserStore} from '@active-user-management';
import {AfterViewInit, ChangeDetectionStrategy, Component, computed, ElementRef, inject, input, OnInit, signal, ViewChild} from '@angular/core';
import {FormControl, FormGroup, FormsModule, ReactiveFormsModule} from '@angular/forms';
import {Base64ImageType, Stamp} from '@core/api/data-access';
import {StoredFile} from '@global-data';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {IconComponent} from '@shared/icon';
import {concatMap, finalize} from 'rxjs';
import {UserFormFactory} from '../../../../services/user-form.factory';
import {ActiveUserStampViewModel} from './active-user-stamp.view-model';
import {MatTooltip} from '@angular/material/tooltip';

// Enum for fixed stamp types
export enum FixedStampType {
	DRAW = 0,
	AUTO = 1,
}

/**
 * This component allows the user to manage their personal stamps:
 * - View available stamps
 * - Add new stamps (upload or draw)
 * - Edit existing stamps
 * - Delete stamps
 * - Select a default stamp
 */
@Component({
	selector: 'dc-active-user-stamps',
	standalone: true,
	styleUrls: ['./active-user-stamps.component.scss', '../../user-form.styles.scss'],
	imports: [ReactiveFormsModule, FormsModule, TranslateModule, IconComponent, MatTooltip],
	templateUrl: './active-user-stamps.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ActiveUserStampsComponent implements OnInit, AfterViewInit {
	private readonly userFormFactory = inject(UserFormFactory);
	private readonly translateService = inject(TranslateService);
	protected readonly activeUserStore = inject(ActiveUserStore);

	@ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
	@ViewChild('stampCanvas') stampCanvas!: ElementRef<HTMLCanvasElement>;

	protected readonly isInvalid = this.userFormFactory.isInvalidFnFactory<'GeneralDetails'>({checkDirty: false});
	readonly parentForm = input.required<FormGroup>();

	// Fixed stamp types for reference in template
	protected readonly FixedStampType = FixedStampType;

	// Signals for state management
	protected readonly loading = signal<boolean>(false);

	protected readonly fixedStamps$ = signal<ActiveUserStampViewModel[]>([
		new ActiveUserStampViewModel({
			name: this.translate('struct.sign-field.draw'),
			index: FixedStampType.DRAW,
			isFixed: true,
			icon: 'fad fa-pencil fa-2x',
		}),
		new ActiveUserStampViewModel({
			name: this.translate('struct.sign-field.autostamp'),
			index: FixedStampType.AUTO,
			isFixed: true,
			icon: 'fad fa-stamp fa-2x',
		}),
	]).asReadonly();

	protected readonly stamps = computed(() => {
		// Get user stamps from the store
		const userStamps =
			this.activeUserStore.stamps$()?.map((stamp, index) => {
				stamp = stamp instanceof Stamp ? stamp : new Stamp(stamp, index + 2); // Offset by 2 for fixed stamps
				const stampModel = new ActiveUserStampViewModel(stamp);
				stampModel.storedFileImage = new StoredFile(stamp.image, stamp.imageType);
				return stampModel;
			}) || [];

		// Return combined stamps array
		return [...this.fixedStamps$(), ...userStamps].removeDuplicates();
	});

	// Modal state signals
	protected readonly addStampModalOpen = signal<boolean>(false);
	protected readonly editStampData = signal<Stamp | null>(null);
	protected readonly stampCreationMode = signal<'upload' | 'draw'>('upload');
	protected readonly stampImagePreview = signal<string | null>(null);

	// Drawing tool signals
	protected readonly drawingTool = signal<'pen' | 'eraser'>('pen');
	protected readonly brushSize = signal<number>(5);
	protected readonly penColor = signal<string>('#000000');
	protected readonly WHITE = '#ffffff' as const;

	// Form data state
	protected stampFormData = signal<{name: string; image: string}>({name: '', image: ''});
	protected patchStampFormData = (value: Partial<{name: string; image: string}>): void => {
		this.stampFormData.update(state => ({...state, ...value}));
	};

	// Helper computed signals
	protected isDefaultStampFactory = (stamp: Stamp) => {
		return computed(() => {
			const defaultStampIndex = this.parentForm().get('defaultStamp')?.value;
			if (defaultStampIndex === null) return false;
			return stamp.index === defaultStampIndex - 2;
		});
	};

	protected readonly canSaveStamp = computed(() => {
		return !!this.stampFormData().name && (this.stampImagePreview() !== null || this.stampCreationMode() === 'draw') && this.stampNameErrors$().length === 0;
	});

	protected stampNameErrors$ = computed<string[]>(() => {
		const errors: string[] = [];
		const allStampNames = this.stamps().map(stamp => stamp.name.toLowerCase());
		const currentName = this.stampFormData().name.toLowerCase();
		if (allStampNames.includes(currentName)) {
			errors.push(this.translate('user.stamps.name-taken'));
		}
		return errors;
	});

	// Store last pen details when switching to eraser
	private _lastPenDetails = {
		color: 'black',
		size: 5,
	};

	ngOnInit(): void {
		this.loadStamps();
	}

	ngAfterViewInit(): void {
		// Initialize canvas if in drawing mode
		if (this.stampCreationMode() === 'draw') {
			this.initializeDrawingCanvas();
		}
	}

	/**
	 * Loads user stamps from the store
	 */
	private loadStamps(): void {
		this.activeUserStore.Stamps.load()
			.pipe(
				finalize(() => {
					this.loading.set(false);
				}),
			)
			.subscribe();
	}

	/**
	 * Opens the modal to add a new stamp
	 */
	protected openAddStampModal(): void {
		this.resetStampForm();
		this.addStampModalOpen.set(true);
	}

	/**
	 * Closes the stamp modal and resets the form
	 */
	protected closeStampModal(): void {
		this.addStampModalOpen.set(false);
		this.editStampData.set(null);
		this.resetStampForm();
	}

	/**
	 * Resets the stamp form data to default values
	 */
	protected resetStampForm(): void {
		this.stampFormData.set({
			name: '',
			image: '',
		});
		this.stampImagePreview.set(null);
		this.stampCreationMode.set('upload');
	}

	/**
	 * Sets the stamp creation mode (upload or draw)
	 */
	protected setStampCreationMode(mode: 'upload' | 'draw'): void {
		this.stampCreationMode.set(mode);
		if (mode === 'draw') {
			this.stampImagePreview.set(null);
			// Initialize drawing canvas in next tick
			setTimeout(() => this.initializeDrawingCanvas(), 0);
		}
	}

	/**
	 * Triggers the file input click event
	 */
	protected triggerFileInput(): void {
		if (this.fileInput?.nativeElement) {
			this.fileInput.nativeElement.click();
		}
	}

	/**
	 * Handles drag over event for file drop
	 */
	protected onDragOver(event: DragEvent): void {
		event.preventDefault();
		event.stopPropagation();
	}

	/**
	 * Handles drag leave event for file drop
	 */
	protected onDragLeave(event: DragEvent): void {
		event.preventDefault();
		event.stopPropagation();
	}

	/**
	 * Handles file drop event
	 */
	protected onDrop(event: DragEvent): void {
		event.preventDefault();
		event.stopPropagation();

		const files = event.dataTransfer?.files;
		if (files && files.length) {
			this.processFile(files[0]);
		}
	}

	/**
	 * Handles file selection from input
	 */
	protected onFileSelected(event: Event): void {
		const input = event.target as HTMLInputElement;
		if (input.files && input.files.length) {
			this.processFile(input.files[0]);
		}
	}

	/**
	 * Processes the selected file and updates the stamp preview
	 */
	private processFile(file: File): void {
		if (!file.type.match(/image.*/)) {
			// Show error that only images are allowed
			return;
		}

		const reader = new FileReader();
		reader.onload = (e: ProgressEvent<FileReader>) => {
			if (e.target?.result) {
				this.stampImagePreview.set(e.target.result as string);
				this.stampFormData.update(state => ({...state, image: e.target.result as string}));
			}
		};
		reader.readAsDataURL(file);
	}

	/**
	 * Initializes the canvas for drawing
	 */
	private initializeDrawingCanvas(): void {
		if (!this.stampCanvas?.nativeElement) return;

		const canvas = this.stampCanvas.nativeElement;
		const context = canvas.getContext('2d');
		if (!context) return;

		// Setup canvas
		canvas.width = canvas.offsetWidth;
		canvas.height = canvas.offsetHeight;
		context.fillStyle = 'white';
		context.fillRect(0, 0, canvas.width, canvas.height);

		let isDrawing = false;

		const startDrawing = (e: MouseEvent | TouchEvent) => {
			isDrawing = true;
			draw(e);
		};

		const stopDrawing = () => {
			isDrawing = false;
			context.beginPath();
			this.saveCanvasAsImage();
		};

		const draw = (e: MouseEvent | TouchEvent) => {
			if (!isDrawing) return;

			context.lineWidth = this.brushSize();
			context.lineCap = 'round';
			context.strokeStyle = this.penColor();

			let clientX: number;
			let clientY: number;

			if ('touches' in e) {
				clientX = e.touches[0].clientX;
				clientY = e.touches[0].clientY;
			} else {
				clientX = e.clientX;
				clientY = e.clientY;
			}

			const rect = canvas.getBoundingClientRect();
			const x = clientX - rect.left;
			const y = clientY - rect.top;

			context.lineTo(x, y);
			context.stroke();
			context.beginPath();
			context.moveTo(x, y);
		};

		// Mouse events
		canvas.addEventListener('mousedown', startDrawing);
		canvas.addEventListener('mousemove', draw);
		canvas.addEventListener('mouseup', stopDrawing);
		canvas.addEventListener('mouseout', stopDrawing);

		// Touch events
		canvas.addEventListener('touchstart', startDrawing);
		canvas.addEventListener('touchmove', draw);
		canvas.addEventListener('touchend', stopDrawing);
	}

	/**
	 * Saves the canvas drawing as an image
	 */
	protected saveCanvasAsImage(): void {
		if (!this.stampCanvas?.nativeElement) return;
		const canvas = this.stampCanvas.nativeElement;
		this.stampFormData.update(state => ({...state, image: canvas.toDataURL('image/png')}));
	}

	/**
	 * Sets the drawing tool (pen or eraser)
	 */
	protected setDrawingTool(tool: 'eraser' | 'pen' | null): void {
		if (tool === null) tool = this.drawingTool() === 'eraser' ? 'pen' : 'eraser';
		switch (tool) {
			case 'eraser':
				this.drawingTool.set(tool);
				this._lastPenDetails.color = this.penColor();
				this._lastPenDetails.size = this.brushSize();
				this.penColor.set(this.WHITE);
				this.brushSize.set(30);
				break;
			case 'pen':
				this.drawingTool.set(tool);
				this.penColor.set(this._lastPenDetails.color);
				this.brushSize.set(this._lastPenDetails.size);
				break;
		}
	}

	/**
	 * Clears the drawing canvas
	 */
	protected clearDrawing(): void {
		if (!this.stampCanvas?.nativeElement) return;

		const canvas = this.stampCanvas.nativeElement;
		const context = canvas.getContext('2d');
		if (context) {
			context.fillStyle = 'white';
			context.fillRect(0, 0, canvas.width, canvas.height);
			this.stampFormData.update(state => ({...state, image: ''}));
		}
	}

	/**
	 * Sets a stamp as the default
	 */
	protected setAsDefaultStamp(stamp: Stamp): void {
		const indexPlusTwo = stamp.index + 2;
		this.activeUserStore.Stamps.setDefaultStamp(stamp).subscribe();
		this.updateDefaultStampInForm(indexPlusTwo);
	}

	/**
	 * Deletes a stamp after confirmation
	 */
	protected deleteStamp(stamp: Stamp): void {
		// Confirm before delete
		if (confirm(this.translate('user.stamps.confirm-delete'))) {
			this.activeUserStore.Stamps.delete(stamp).subscribe(() => {
				// If the deleted stamp was the default, reset the default
				if (this.isDefaultStampFactory(stamp)()) {
					this.updateDefaultStampInForm(0);
				}
			});
		}
	}

	/**
	 * Saves a new stamp or updates an existing one
	 */
	protected saveStamp(): void {
		if (!this.canSaveStamp()) return;

		if (this.editStampData()) {
			// Update existing stamp
			const stamp = this.editStampData();
			if (stamp) {
				const updatedStamp = new ActiveUserStampViewModel({
					...stamp,
					name: this.stampFormData().name,
					image: this.stampFormData().image || stamp.image,
					imageType: Base64ImageType.PNG,
				});

				this.activeUserStore.Stamps.delete(stamp)
					.pipe(concatMap(() => this.activeUserStore.Stamps.create(updatedStamp)))
					.subscribe();
			}
		} else {
			// Add a new stamp
			const newStamp = new ActiveUserStampViewModel({
				name: this.stampFormData().name,
				image: this.stampFormData().image,
				imageType: Base64ImageType.PNG,
				index: this.stamps().length,
			});

			this.activeUserStore.Stamps.create(newStamp.toJSON()).subscribe(response => {});
		}

		this.closeStampModal();
	}

	/**
	 * Updates the default stamp in the form
	 */
	private updateDefaultStampInForm(indexPlusTwo: number): void {
		const defaultStampControl = this.parentForm().get('defaultStamp') as FormControl<number>;
		if (defaultStampControl) {
			defaultStampControl.setValue(indexPlusTwo);
		}
	}

	/**
	 * Helper method to translate strings
	 */
	private translate(key: string): string {
		return this.translateService.instant(key);
	}
}
